// Pure rules behind the match score sheet (scorecard page and the sheet under
// the Watch video): which games a match has, and one column per rally, marker
// and conduct action of each game. Components render exactly what this returns.
import type { Tables } from '@/types/supabase';
import { createTimelineActions, type ScorecardPoint } from '@/lib/scorecard/timeline';
import { tryResolveAppState, toScorecardGames } from '@/lib/scorecard/resolve';
import { pointsForGame } from './gameTarget';

export type MatchWithGames = Tables<'match'> & {
  match_game?: GameRow[];
};

/** A stored `match_game` row, or a game resolved from a live `app_state`. */
export type GameRow = Tables<'match_game'> & {
  match_game_point?: Tables<'match_game_point'>[];
  /** Set on games resolved from `app_state` only: false for the game being played. */
  completed?: boolean;
};

export type SheetSide = 0 | 1;
export type SheetMarker = 'let' | 'noLet' | 'stroke';
export type ConductLevel = 'warning' | 'conduct_stroke' | 'conduct_game' | 'conduct_match';
export type SheetCell = { score: number | null; box: 'L' | 'R' | null };
export type SheetColumn =
  | {
      kind: 'rally';
      id: string;
      cells: [SheetCell, SheetCell];
      score: [number, number];
      scorer: SheetSide;
    }
  | { kind: 'marker'; id: string; marker: SheetMarker }
  | { kind: 'conduct'; id: string; side: SheetSide; level: ConductLevel };

export interface SheetGame {
  number: number;
  durationSeconds: number | null;
  score: [number, number];
  winner: SheetSide | null;
  inProgress: boolean;
  columns: SheetColumn[];
}

export interface ResolvedMatchGames {
  games: GameRow[];
  gamesWon: [number, number];
}

/**
 * Live: resolve from `app_state` with the save chain's rules, so the page shows
 * exactly what finalize will persist. Unusable blob, or not live: stored rows.
 */
export function resolveMatchGames(match: MatchWithGames): ResolvedMatchGames {
  let gamesWon: [number, number] = [match.player_1_games || 0, match.player_2_games || 0];
  let games: GameRow[] = match.match_game || [];

  if (match.status === 'in_progress') {
    const resolved = tryResolveAppState(match.app_state);
    if (resolved && resolved.games.length > 0) {
      // toScorecardGames drops the duration and the completed flag; both come
      // from the same resolved game, so carry them across by position.
      games = toScorecardGames(resolved).map((g, i) => ({
        ...g,
        duration_seconds: resolved.games[i].duration_seconds,
        completed: resolved.games[i].completed,
      })) as unknown as GameRow[];
      gamesWon = [resolved.player_1_games, resolved.player_2_games];
    }
  }
  return { games, gamesWon };
}

const sideOf = (s: string | null | undefined): SheetSide => (s === 'player_2' ? 1 : 0);

const CONDUCT_LEVELS: ConductLevel[] = [
  'warning',
  'conduct_stroke',
  'conduct_game',
  'conduct_match',
];

function buildColumns(points: ScorecardPoint[], target: number): SheetColumn[] {
  const columns: SheetColumn[] = [];
  // createTimelineActions is most recent first; the sheet reads oldest first.
  const actions = createTimelineActions(points, target).reverse();
  for (const a of actions) {
    if (a.type === 'conduct') {
      const level = a.metadata?.conductLevel;
      columns.push({
        kind: 'conduct',
        id: a.id,
        side: sideOf(a.scoringPlayer),
        level: level && CONDUCT_LEVELS.includes(level) ? level : 'warning',
      });
    } else if (a.type === 'let_call') {
      columns.push({ kind: 'marker', id: a.id, marker: 'let' });
    } else if (a.metadata?.isNoLet) {
      columns.push({ kind: 'marker', id: a.id, marker: 'noLet' });
    } else if (a.metadata?.isStroke) {
      columns.push({ kind: 'marker', id: a.id, marker: 'stroke' });
    } else if (a.scoringPlayer) {
      const scorer = sideOf(a.scoringPlayer);
      const server = sideOf(a.servingPlayer);
      const score: [number, number] = [a.player1Score, a.player2Score];
      const cells: [SheetCell, SheetCell] = [
        { score: null, box: null },
        { score: null, box: null },
      ];
      cells[scorer].score = score[scorer];
      cells[server].box = a.serviceBoxSide;
      columns.push({ kind: 'rally', id: a.id, cells, score, scorer });
    }
  }
  return columns;
}

/** A game's length as minutes:seconds ("3:25"); empty for none. No hours form: 62:15 stays minutes. */
export const formatGameDuration = (seconds: number | null) => {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/** One sheet game per row. `matchLive` is true only while the match is in progress. */
export function buildSheetGames(
  games: GameRow[],
  format: { bestOf: number; pointsPerGame: number; decidingGamePoints?: number | null },
  matchLive: boolean,
): SheetGame[] {
  const ordered = [...games].sort((a, b) => a.game_number - b.game_number);
  return ordered.map((g, i) => {
    const score: [number, number] = [g.player_1_score, g.player_2_score];
    const target = pointsForGame(format, g.game_number);
    const isLast = i === ordered.length - 1;
    const decided = Math.max(score[0], score[1]) >= target && Math.abs(score[0] - score[1]) >= 2;
    // A resolved game says whether it has been archived; stored rows have no
    // such field, so the target and a two-point lead decide.
    const inProgress =
      matchLive && isLast && (typeof g.completed === 'boolean' ? !g.completed : !decided);
    const winner: SheetSide | null = inProgress
      ? null
      : score[0] > score[1]
        ? 0
        : score[1] > score[0]
          ? 1
          : null;
    const points = (g.match_game_point ?? []) as unknown as ScorecardPoint[];
    return {
      number: g.game_number,
      durationSeconds: g.duration_seconds ?? null,
      score,
      winner,
      inProgress,
      columns: buildColumns(points, target),
    };
  });
}

/** A match's format, with the defaults a row with empty format columns plays to. */
export function matchFormatOf(
  match: Pick<MatchWithGames, 'best_of' | 'points_per_game' | 'deciding_game_points'>,
): { bestOf: number; pointsPerGame: number; decidingGamePoints: number | null } {
  return {
    bestOf: match.best_of || 5,
    pointsPerGame: match.points_per_game || 11,
    decidingGamePoints: match.deciding_game_points,
  };
}

/**
 * The one pipeline from a match row to its sheet, shared by the scorecard page and the Watch panel,
 * so the two can never read a match's games, targets or live game differently.
 */
export function sheetGamesFor(match: MatchWithGames): {
  sheetGames: SheetGame[];
  gamesWon: [number, number];
} {
  const { games, gamesWon } = resolveMatchGames(match);
  return {
    sheetGames: buildSheetGames(games, matchFormatOf(match), match.status === 'in_progress'),
    gamesWon,
  };
}
